import { lazy, Suspense } from 'react';
import LogDeclineSheet from './LogDeclineSheet.jsx';
import QuoteEntrySheet from './QuoteEntrySheet.jsx';
import LogHangarQuoteSheet from './LogHangarQuoteSheet.jsx';
import LogPropertyQuoteSheet from './LogPropertyQuoteSheet.jsx';
import LogGlQuoteSheet from './LogGlQuoteSheet.jsx';
import { resolveLine } from '@/lib/insurance-line.js';
import { HANGAR_SUBMISSION_UPDATED_EVENT } from '@/components/account/SubmissionPanel.jsx';
// StatusTransitionSheet is retired (DEAL-LIFECYCLE-SPEC Phase 1): lifecycle
// state derives from ground truth; the manual Mark presented/paid/bound
// doors are gone.
import PaperDraftsSheet from './PaperDraftsSheet.jsx';
// PreStageRenewalSheet is the unified Initiate Renewal surface (replaced
// InitiateRenewalDialog 2026-06-04). Lazy-loaded to keep its ~35KB chunk
// out of the main bundle — only fetched when the broker opens it.
const PreStageRenewalSheet = lazy(() => import('./PreStageRenewalSheet.jsx'));
// RosterSheet shares RosterEditor with PreStageRenewalSheet — lazy-loaded
// for the same bundle reason. Mounted at EVERY stage (visibility-
// unconditional; stage only informs the copy inside the sheet).
const RosterSheet = lazy(() => import('./RosterSheet.jsx'));
// QuoteDiffSheet — quote-drop diff (FLEET-ROSTER-SPEC.md Layer 3). Lazy
// for the same bundle reason; mounted at every stage like RosterSheet.
const QuoteDiffSheet = lazy(() => import('./QuoteDiffSheet.jsx'));
import EditNamedInsuredSheet from './EditNamedInsuredSheet.jsx';
import SnklRequestDialog from './SnklRequestDialog.jsx';
import BrRequestDialog from './BrRequestDialog.jsx';
import CaRequestDialog from './CaRequestDialog.jsx';
import LPRSheet from './LPRSheet.jsx';
import PolicyInfoDialog from './PolicyInfoDialog.jsx';
import LimitCorrectionDialog from './LimitCorrectionDialog.jsx';
import COISheet from './COISheet.jsx';
import PaymentRequestSheet from './PaymentRequestSheet.jsx';
import PropertyRenewalDialog from './PropertyRenewalDialog.jsx';
import RewriteDealSheet from './RewriteDealSheet.jsx';
import SendBundleDialog from './SendBundleDialog.jsx';
import CarrierConfirmDialog from './CarrierConfirmDialog.jsx';
import PolicyDocsDropSheet from './PolicyDocsDropSheet.jsx';
import BindHoldSheet from './BindHoldSheet.jsx';
import ComposeSheet from '@/components/comms/ComposeSheet.jsx';
import WelcomeTextSheet from '@/components/intake/WelcomeTextSheet.jsx';
import ResumeLinkSheet from '@/components/intake/ResumeLinkSheet.jsx';
// RTQ dispatch (MV-02 2.4) — lazy: it pulls PreviewPane + a full email render.
const RtqDispatchSheet = lazy(() => import('./RtqDispatchSheet.jsx'));
import { recommendedForContext } from '@/components/comms/stageTemplates.js';

// Render-only component that mounts every per-deal sheet/modal and
// wires their open state to a useDealActions() return value. Used by
// both DealActionBar (account workspace footer) and PipelineDealCard
// (kanban card ⋯ menu).
//
// 2026-05-20 Vision §V2 fix #6: prior two-surface quote flow
// (LogQuoteReceivedSheet + QuoteEntryModal) collapsed into a single
// QuoteEntrySheet. Save+Present hand-off uses the actionState's
// openComposeWithTemplate helper to open ComposeSheet at workspace
// level with 'quotes-ready' pre-selected — no stacked overlays.

export default function DealActionsSheets({ dealId, actionState, onChange }) {
  const {
    crmStage,
    lifecycleState,
    sessionToken,
    dealContext,
    item,
    openComposeWithTemplate,
    sheetState: {
      composeOpen,
      setComposeOpen,
      composeInitialSlug,
      setComposeInitialSlug,
      declineOpen,
      setDeclineOpen,
      quoteOpen,
      setQuoteOpen,
      renewOpen,
      setRenewOpen,
      bindingOpen,
      setBindingOpen,
      snklOpen,
      setSnklOpen,
      lprOpen,
      setLprOpen,
      lprPrefill,
      setLprPrefill,
      coiOpen,
      setCoiOpen,
      paymentRequestOpen,
      setPaymentRequestOpen,
      uploadDocsOpen,
      setUploadDocsOpen,
      sendBundleOpen,
      setSendBundleOpen,
      carrierConfirmOpen,
      setCarrierConfirmOpen,
      rosterOpen,
      setRosterOpen,
      quoteDiffOpen,
      setQuoteDiffOpen,
      editInsuredOpen,
      setEditInsuredOpen,
      policyInfoOpen,
      setPolicyInfoOpen,
      limitCorrectionOpen,
      setLimitCorrectionOpen,
      coOwnerPartnership,
      setCoOwnerPartnership,
      paperDraftsOpen,
      setPaperDraftsOpen,
      welcomeTextOpen,
      setWelcomeTextOpen,
      resumeLinkOpen,
      setResumeLinkOpen,
      rtqOpen,
      setRtqOpen,
      rewriteOpen,
      setRewriteOpen,
      bindHoldOpen,
      setBindHoldOpen,
    },
  } = actionState;

  // Line-routed quote door (Ship 0 follow-on): item is the pipeline-item
  // shape and carries Insurance_Line + BR fields; a null item (still
  // loading) resolves Aviation, which is fine — the sheet only opens on a
  // user action after the deal has loaded.
  const line = resolveLine(item);

  function handleQuoteSaved(result) {
    // Save+Present hand-off: quote sheet has already closed itself
    // (Vision §V2 fix #6 — no stacked Sheet-on-Sheet); open the
    // shared ComposeSheet with the 'quotes-ready' template
    // pre-selected so the broker is one click from sending.
    if (result?.present) {
      openComposeWithTemplate('quotes-ready');
    }
    if (typeof onChange === 'function') onChange();
  }

  return (
    <>
      <LogDeclineSheet
        dealId={dealId}
        open={declineOpen}
        onOpenChange={setDeclineOpen}
        onLogged={onChange}
      />
      {/* The shared "quote" verb (rail Enter a quote, overflow Log quote,
          state primary) routes by line: an aircraft-shaped QuoteEntrySheet on
          a property deal was a dead end (Setman 2026-07-31). Same sheets the
          line cockpits mount; same props contract. */}
      {line.isHangar ? (
        <LogHangarQuoteSheet
          dealId={dealId}
          open={quoteOpen}
          onOpenChange={setQuoteOpen}
          onSaved={() => {
            window.dispatchEvent(new CustomEvent(HANGAR_SUBMISSION_UPDATED_EVENT, { detail: { dealId } }));
            if (typeof onChange === 'function') onChange();
          }}
        />
      ) : line.isBuildersRisk ? (
        <LogPropertyQuoteSheet
          dealId={dealId}
          open={quoteOpen}
          onOpenChange={setQuoteOpen}
          onSaved={onChange}
        />
      ) : line.isCommercial ? (
        <LogGlQuoteSheet
          dealId={dealId}
          open={quoteOpen}
          onOpenChange={setQuoteOpen}
          onSaved={onChange}
        />
      ) : (
        <QuoteEntrySheet
          dealId={dealId}
          open={quoteOpen}
          onOpenChange={setQuoteOpen}
          onSaved={handleQuoteSaved}
        />
      )}
      <PaperDraftsSheet
        dealId={dealId}
        open={paperDraftsOpen}
        onOpenChange={setPaperDraftsOpen}
        onChange={onChange}
      />
      <ComposeSheet
        sessionToken={sessionToken}
        dealId={dealId}
        open={composeOpen}
        onOpenChange={(o) => {
          setComposeOpen(o);
          // Clear the hand-off slug when ComposeSheet closes so the
          // next Send action doesn't reopen pre-selected to the
          // prior template.
          if (!o) setComposeInitialSlug(null);
        }}
        initialSlug={composeInitialSlug}
        dealContext={dealContext || { stage: crmStage }}
        onSent={onChange}
      />
      {renewOpen && resolveLine(item || {}).isProperty && (
        <PropertyRenewalDialog
          open={renewOpen}
          onOpenChange={setRenewOpen}
          dealId={dealId}
          accountId={item?.account_id || item?.crm_account_id || item?.Account_Name?.id}
          initialAccountName={item?.named_insured || 'this deal'}
          initialEffective={item?.policy_expiration || item?.policy_end || ''}
          onRenewed={() => onChange?.()}
        />
      )}
      {renewOpen && !resolveLine(item || {}).isProperty && (
        <Suspense fallback={null}>
          <PreStageRenewalSheet
            intent="initiate"
            open={renewOpen}
            onOpenChange={setRenewOpen}
            dealId={dealId}
            accountId={item?.account_id || item?.crm_account_id || item?.Account_Name?.id}
            initialAccountName={item?.named_insured || 'this deal'}
            initialAircraft={item?.aircraft || []}
            initialPremium={item?.premium}
            onPreStaged={() => { setRenewOpen(false); onChange?.(); }}
          />
        </Suspense>
      )}
      {(item?.Insurance_Line === 'Commercial' || item?.Builders_Risk_Data || item?.BR_Status) ? (
        item?.Insurance_Line === 'Commercial'
          ? <CaRequestDialog dealId={dealId} open={bindingOpen} onOpenChange={setBindingOpen} onChange={onChange} />
          : <BrRequestDialog dealId={dealId} open={bindingOpen} onOpenChange={setBindingOpen} onChange={onChange} />
      ) : null}
      <EditNamedInsuredSheet
        accountId={item?.account_id || item?.crm_account_id || item?.Account_Name?.id}
        // Always pass the deal context: the server cascades the rename into
        // the application overlay + packet regen ONLY when the deal's session
        // is a hangar intake (a no-op on aviation deals). Client-side gating
        // on item.Insurance_Line would silently skip card shapes that don't
        // carry the field.
        cascadeDealId={dealId}
        initialName={item?.named_insured || ''}
        // Co-owner chain: when an Owner was just added, default the entity to
        // Partnership (David: co-ownership = Partnership) instead of the
        // account's current type.
        initialPolicyholderType={coOwnerPartnership ? 'Partnership' : (item?.policyholder_type || '')}
        open={editInsuredOpen}
        onOpenChange={(o) => {
          setEditInsuredOpen(o);
          if (!o) setCoOwnerPartnership(false);
        }}
        onSaved={onChange}
      />
      <SnklRequestDialog
        dealId={dealId}
        accountName={item?.named_insured}
        open={snklOpen}
        onOpenChange={setSnklOpen}
        onChange={onChange}
      />
      <LPRSheet
        dealId={dealId}
        open={lprOpen}
        onOpenChange={(o) => {
          setLprOpen(o);
          if (!o) setLprPrefill(null);
        }}
        prefill={lprPrefill}
        onChange={onChange}
      />
      {/* Policy info door (TRACON-FIELD-GAP-INVENTORY.md): one component,
          also mounted inside LPRSheet for the fix-it-in-place path. */}
      <PolicyInfoDialog
        dealId={dealId}
        open={policyInfoOpen}
        onOpenChange={setPolicyInfoOpen}
        initial={{
          policy_number: item?.policy_number,
          policy_start: item?.policy_start,
          policy_end: item?.policy_expiration,
          carrier_name: item?.carrier,
          producer_id: item?.producer_id,
          producer_name: item?.producer_name,
        }}
        onSaved={onChange}
      />
      <LimitCorrectionDialog
        dealId={dealId}
        open={limitCorrectionOpen}
        onOpenChange={setLimitCorrectionOpen}
        onSaved={onChange}
      />
      <COISheet
        dealId={dealId}
        accountId={item?.account_id || item?.crm_account_id || item?.Account_Name?.id}
        open={coiOpen}
        onOpenChange={setCoiOpen}
        onChange={onChange}
      />
      <PaymentRequestSheet
        dealId={dealId}
        open={paymentRequestOpen}
        onOpenChange={setPaymentRequestOpen}
        onChange={onChange}
      />
      <RewriteDealSheet
        dealId={dealId}
        item={item}
        // The normalized state (focused-deal payload or kanban approximation),
        // not item.lifecycle_state, which exists on only one of the two item
        // shapes (LAPSED-CLIENT-REUP-SPEC §3.0).
        lifecycleState={lifecycleState}
        open={rewriteOpen}
        onOpenChange={setRewriteOpen}
      />
      <WelcomeTextSheet
        dealId={dealId}
        open={welcomeTextOpen}
        onOpenChange={setWelcomeTextOpen}
        onChange={onChange}
      />
      <ResumeLinkSheet
        dealId={dealId}
        open={resumeLinkOpen}
        onOpenChange={setResumeLinkOpen}
        onChange={onChange}
      />
      {rtqOpen && (
        <Suspense fallback={null}>
          <RtqDispatchSheet
            dealId={dealId}
            open={rtqOpen}
            onOpenChange={setRtqOpen}
            onChange={onChange}
          />
        </Suspense>
      )}
      <PolicyDocsDropSheet
        dealId={dealId}
        open={uploadDocsOpen}
        onOpenChange={setUploadDocsOpen}
        onSuccess={() => {
          onChange?.();
          setUploadDocsOpen(false);
        }}
      />
      {/* 200 §2: the flip. It records; the packet is OFFERED afterwards by
          opening the existing publish dialog, never sent automatically. */}
      <CarrierConfirmDialog
        dealId={dealId}
        open={carrierConfirmOpen}
        onOpenChange={setCarrierConfirmOpen}
        onConfirmed={onChange}
        onOfferPacket={() => setSendBundleOpen(true)}
      />
      <SendBundleDialog
        dealId={dealId}
        accountName={item?.named_insured}
        open={sendBundleOpen}
        onOpenChange={setSendBundleOpen}
        onChange={onChange}
      />
      {rosterOpen && (
        <Suspense fallback={null}>
          <RosterSheet
            dealId={dealId}
            open={rosterOpen}
            onOpenChange={(o) => {
              setRosterOpen(o);
              // Co-owner chain (PILOT-DOORS Ship B): an Owner added through
              // the roster's add card flags coOwnerPartnership; the
              // named-insured / entity prompt opens AFTER the roster sheet
              // closes — never stacked on top of it.
              if (!o && coOwnerPartnership) setEditInsuredOpen(true);
            }}
            accountName={item?.named_insured}
            stage={item?.stage || crmStage}
            onChange={onChange}
            onPilotAdded={({ interest }) => {
              if (interest === 'Owner') setCoOwnerPartnership(true);
            }}
            onLprRequired={(tails) => {
              // Paper-trail gate hand-off: roster removal on a bound policy
              // routes into the scoped LPR wizard with the tails prefilled.
              setLprPrefill({ scope: 'aircraft', scope_tails: tails });
              setLprOpen(true);
            }}
          />
        </Suspense>
      )}
      {/* Decision 8: the signing hold sheet. It reads the holds when it opens
          and re-reads after a decision; onChange refreshes the queue rows. */}
      <BindHoldSheet
        dealId={dealId}
        open={bindHoldOpen}
        onOpenChange={setBindHoldOpen}
        onChange={onChange}
      />
      {quoteDiffOpen && (
        <Suspense fallback={null}>
          <QuoteDiffSheet
            dealId={dealId}
            open={quoteDiffOpen}
            onOpenChange={setQuoteDiffOpen}
            accountName={item?.named_insured}
            onChange={onChange}
            onEnterManually={() => {
              // Low-confidence fallback: close the diff sheet, open the
              // manual QuoteEntrySheet (same no-stacked-overlays pattern
              // as the Save+Present hand-off above).
              setQuoteDiffOpen(false);
              setQuoteOpen(true);
            }}
          />
        </Suspense>
      )}
    </>
  );
}

// Re-export so consumers that already pull from this file get the
// recommender for free if they need to compute context outside the
// sheets (e.g. for ⋯ menu hover labels).
export { recommendedForContext };
